<template>
  <div
    class="ce-multiFilter"
    :class="{'is-open': isOpen, disabled: disabled }">
    <div
      class="ce-multiFilter-toggle flex"
      @click="toggle">
      <div class="flex-auto">
        {{ label }}
        <span
          class="ms-1 ml1"
          v-if="selectedCount > 0">
          ({{ selectedCount }} {{ $t('filter.selected') }})
        </span>
      </div>
      <div class="ce-multiFilter-toggleIndicator">
        <fa-icon :icon="['fas', 'angle-down']" />
      </div>
    </div>

    <div
      v-show="isOpen"
      class="ce-multiFilter-content">
      <div class="ce-multiFilter-contentHeader">
        <a
          href
          @click.prevent="reset">
          {{ $t('filter.reset') }}
        </a>
      </div>

      <div class="ce-multiFilter-options">
        <div
          class="ce-multiFilter-option"
          v-for="el in internalOptions"
          :key="el.code"
          :class="{disabled: el.disabled}">
          <checkbox
            :id="`code-${el.code}`"
            :model-value="el.selected"
            :disabled="el.disabled"
            @update:model-value="toggleValue(el, $event)">
            {{ el.title }}
          </checkbox>
        </div>
      </div>

      <div
        class="ce-multiFilter-footer"
        v-if="isCollectMode">
        <button
          @click.prevent="save"
          class="button-primaryLight">
          {{ $t('filter.save') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import DropDownMixin from 'src/mixins/DropDown';
import Checkbox from 'components/Checkbox.vue';
import { sortArray, sortColorIndexArray } from 'utils';

/**
 * Class for a single "multi filter".
 *
 * Currently just a list of checkboxes, but could also be for example a multi select control.
 */
export default {
  props: {
    options: {
      type: Array,
      required: true
    },
    label: {
      type: String,
      required: true
    },
    filterKey: {
      type: String,
      required: true
    },

    /**
     * Defines the behaviour when interacting with options.
     *
     * 'instant' triggers a search on each click, 'collect' waits until the save button is clicked
     */
    mode: {
      type: String,
      default: 'instant'
    },

    emitOnly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      internalOptions: null,
      containerClass: 'ce-multiFilter'
    };
  },
  mixins: [DropDownMixin],
  components: { Checkbox },
  computed: {
    ...mapGetters(['urlFilters']),
    selectedCount() {
      const options = this.internalOptions ? this.internalOptions : this.options;

      return options.filter((option) => option.selected).length;
    },
    isInstantMode() {
      return this.mode === 'instant';
    },
    isCollectMode() {
      return this.mode === 'collect';
    },
    disabled() {
      return this.options.filter((o) => !o.disabled).length === 0;
    }
  },
  methods: {
    open() {
      if (this.filterKey === 'color_index') {
        this.internalOptions = sortColorIndexArray(this.$cloneObject(this.options));
      } else {
        this.internalOptions = sortArray(this.$cloneObject(this.options), 'title', 'asc');
      }

      this.isOpen = true;
      this.setActiveDropDownFilter(this._uid);
    },
    close() {
      this.isOpen = false;
      this.internalOptions = null;
    },
    reset() {
      this.internalOptions.forEach((option) => {
        option.selected = false;
      });
      this.save();

      // Close after reset, only necessary if inInstantMode, otherwise done in the save method
      if (this.isInstantMode) this.close();
    },
    toggleValue(el, value) {
      el.selected = value;

      this.$trackEvent('filter', 'toggle', {
        filterType: 'multi',
        filterKey: this.filterKey,
        filterLabel: this.label,
        value: el.title,
        selected: el.selected
      });

      if (this.isInstantMode) this.save();
    },
    save() {
      const selected = this.internalOptions
        .filter((option) => option.selected)
        .map((option) => option.code);

      if (this.emitOnly) {
        this.$emit('update:modelValue', selected);
        if (!this.isInstantMode) this.close();
      } else {
        const url = this.$cloneObject(this.urlFilters);

        if (selected.length > 0) url[this.filterKey] = selected;
        else delete url[this.filterKey];

        this.$store.dispatch('setUrlFilters', url).then(() => {
          if (!this.isInstantMode) this.close();
        });
      }
    }
  }
};
</script>

<style lang="less">
  @import (reference) "~styles/includes/base";

  .ce-multiFilter {
    position: relative;
    margin-bottom: @space-3;

    .ce-multiFilter-toggle {
      background: @ce-filter-background-color;
      border: 1px solid @ce-filter-border-color;
      color: @ce-filter-color;
      font-weight: 500;
      padding: @space-2 @space-3;
      border-radius: @border-radius;
      cursor: pointer;
      .noselect();

      .ce-multiFilter-toggleIndicator {
        transition: transform 250ms ease;
        color: @ce-filter-highlight-color;

        svg {
          pointer-events: none;
        }
      }
    }

    &.is-open {
      .ce-multiFilter-toggle {
        .ce-multiFilter-toggleIndicator {
          transform: rotate(-180deg);
        }
      }
    }

    &.disabled {
      display: none;
    }

    .ce-multiFilter-content {
      position: absolute;
      top: 100%;
      margin-top: @space-1;
      left: 0;
      width: 100%;
      background: @color-mono-100;
      z-index: 1000;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      color: @color-mono-20;
      border-radius: @border-radius;
      border: 2px solid darken(@ce-filter-background-color, 10);

      .ce-multiFilter-contentHeader {
        flex: none;
        text-align: right;
        padding: @space-2;
        margin-bottom: @space-2;
        border-bottom: 1px solid darken(@color-mono-95, 10);

        a {
          color: @color-mono-20;
          border-bottom-color: transparent;

          &:hover {
            border-bottom-color: @color-main;
          }
        }
      }
      .ce-multiFilter-options {
        flex: 1 1 auto;
        max-height: 250px;
        overflow-y: auto;
        margin-bottom: @space-2;
        color: @ce-filter-color;

        .ce-multiFilter-option {
          padding: @space-2;
          cursor: pointer;
          .noselect();

          &.disabled {
            display: none;
          }

          &:hover {
            background: darken(@color-mono-95, 5);
          }
        }
      }
      .ce-multiFilter-footer {
        flex: none;

        button {
          display: block;
          width: 100%;
        }
      }
    }
  }
</style>
